import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getPendingShiftSwaps } from '../../../services/shiftSwapService';
import { formatDate, extractListData } from '../../../utils/Utils';
import PersonCell from '../../../components/ui/PersonCell';

const ShiftLine = styled.div`
  line-height: 1.4;

  &:not(:last-child) {
    margin-bottom: 2px;
  }
`;

const ShiftName = styled.span`
  color: #6b7280;
  margin-left: 6px;
`;

// Task 8'in shiftSwapService.js'i — /shift-swaps/pending düz List<ShiftSwapResponse>
// döner (sayfalama YOK), Task 9'un useMyShiftSwapApi.js'i ile birebir aynı düz-liste
// deseni: paramsız tek fetch + refreshTrigger. ShiftSwapApprovalsPage bu tabloyu
// DataTable'a serverSide={false} ile verir (client-side sayfalama/filtre/sıralama).
//
// Uyum önizleme rozeti ve aksiyonlar sütunu SAYFA state'i (seçili önizleme, onay/red
// diyalogları) gerektirdiği için burada değil — PendingApprovalsPage.js/usePendingLeaveApi.js
// deseninde olduğu gibi (actions sütunu hook'ta değil sayfada baseColumns'a eklenir)
// ShiftSwapApprovalsPage.js'te baseColumns'a eklenir.
export const usePendingShiftSwapApi = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    setFetching(true);
    getPendingShiftSwaps()
      .then((res) => setData(extractListData(res)))
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const typeLabels = useMemo(() => ({
    trade: t('shiftSwap.type.trade'),
    cover: t('shiftSwap.type.cover'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'type',
      label: t('shiftSwap.approvals.type'),
      options: {
        filter: true, sort: true,
        filterType: 'dropdown',
        filterOptions: { names: ['trade', 'cover'], renderValue: (v) => typeLabels[v] || v },
        customFilterListOptions: { render: (v) => typeLabels[v] || v },
        customBodyRender: (v) => typeLabels[v] || v,
      },
    },
    {
      name: 'requesterFullName',
      label: t('shiftSwap.approvals.requester'),
      options: {
        filter: true, sort: true, filterType: 'textField',
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.requesterProfilePicture} />
        ),
      },
    },
    {
      name: 'targetFullName',
      label: t('shiftSwap.approvals.target'),
      options: {
        filter: true, sort: true, filterType: 'textField',
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.targetProfilePicture} />
        ),
      },
    },
    {
      name: 'requesterShiftDate',
      label: t('shiftSwap.approvals.shifts'),
      options: {
        filter: false, sort: true,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return (
            <div>
              <ShiftLine>
                {formatDate(row.requesterShiftDate, lang)}
                {row.requesterShiftName && <ShiftName>{row.requesterShiftName}</ShiftName>}
              </ShiftLine>
              {row.type === 'trade' && row.targetShiftDate && (
                <ShiftLine>
                  {formatDate(row.targetShiftDate, lang)}
                  {row.targetShiftName && <ShiftName>{row.targetShiftName}</ShiftName>}
                </ShiftLine>
              )}
            </div>
          );
        },
      },
    },
  ], [t, data, lang, typeLabels]);

  return { columns, data, fetching, refresh };
};
